import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStaff, useDebouncedValue } from '../../hooks/useStaff.js';
import { StaffCard } from '../../components/dashboard/StaffCard.jsx';
import { EmptyState } from '../../components/common/EmptyState.jsx';

/**
 * Staff list page. The main workspace for tenant admins.
 *
 * Features:
 *  - Stat strip at top (active / suspended / left counts)
 *  - Search input (debounced 300ms)
 *  - Status filter tabs (All / Active / Suspended / Left)
 *  - Grid of StaffCards
 *  - Empty state when there are no staff yet, or filter produces no matches
 *  - Skeleton loading state
 *
 * The stats are computed client-side from the full staff list. We could
 * hit a dedicated stats endpoint, but for list sizes under ~1000 it's
 * cheaper just to count in the browser than make a second API call.
 */
export function StaffPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(null); // null = all

  const debouncedSearch = useDebouncedValue(search, 300);

  // Fetch once without filters so stats reflect the full picture.
  // Then apply search + status filtering client-side. API-side filtering
  // would break the stats.
  const { staff: allStaff, loading, error, refresh } = useStaff({});

  const stats = useMemo(() => {
    const counts = { total: 0, active: 0, suspended: 0, left: 0 };
    for (const s of allStaff) {
      counts.total += 1;
      if (s.status in counts) counts[s.status] += 1;
    }
    return counts;
  }, [allStaff]);

  const visibleStaff = useMemo(() => {
    const term = debouncedSearch.trim().toLowerCase();
    return allStaff.filter(s => {
      if (statusFilter && s.status !== statusFilter) return false;
      if (!term) return true;
      const first = (s.first_name || '').toLowerCase();
      const last = (s.last_name || '').toLowerCase();
      const role = (s.role || '').toLowerCase();
      return first.includes(term) || last.includes(term) || role.includes(term);
    });
  }, [allStaff, debouncedSearch, statusFilter]);

  return (
    <>
      <div className="page-header">
        <div className="page-title-wrap">
          <h1 className="page-title">Staff</h1>
          <p className="page-subtitle">
            Your team — everyone who represents your business when they're
            scanned.
          </p>
        </div>
        <div className="page-actions">
          <Link to="/dashboard/staff/new" className="btn btn-primary">
            Add staff member
          </Link>
        </div>
      </div>

      {error && <LoadError onRetry={refresh} />}

      {!error && (
        <>
          <StatStrip stats={stats} />

          <div className="toolbar" role="search">
            <div className="toolbar-search">
              <svg className="toolbar-search-icon" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="11" cy="11" r="7" />
                <path d="M20 20l-3-3" />
              </svg>
              <input
                type="search"
                placeholder="Search by name or role…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                aria-label="Search staff"
              />
            </div>

            <FilterButton
              label="All"
              active={statusFilter === null}
              onClick={() => setStatusFilter(null)}
            />
            <FilterButton
              label="Active"
              count={stats.active}
              active={statusFilter === 'active'}
              onClick={() => setStatusFilter('active')}
            />
            <FilterButton
              label="Suspended"
              count={stats.suspended}
              active={statusFilter === 'suspended'}
              onClick={() => setStatusFilter('suspended')}
            />
            <FilterButton
              label="Left"
              count={stats.left}
              active={statusFilter === 'left'}
              onClick={() => setStatusFilter('left')}
            />
          </div>

          {loading && <SkeletonGrid />}

          {!loading && visibleStaff.length === 0 && (
            <StaffEmpty
              hasAnyStaff={stats.total > 0}
              searchTerm={debouncedSearch.trim()}
              statusFilter={statusFilter}
            />
          )}

          {!loading && visibleStaff.length > 0 && (
            <div className="staff-grid">
              {visibleStaff.map(s => (
                <StaffCard key={s.id} staff={s} />
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

/* ─────────────────────────────────────────────────────────── */

function StatStrip({ stats }) {
  return (
    <div className="stat-strip">
      <div className="stat-card">
        <span className="stat-card-label">Total</span>
        <div className="stat-card-value">{stats.total}</div>
      </div>
      <div className="stat-card">
        <span className="stat-card-label">Active</span>
        <div className="stat-card-value" style={{ color: 'var(--tocyn-green-d)' }}>
          {stats.active}
        </div>
      </div>
      <div className="stat-card">
        <span className="stat-card-label">Suspended</span>
        <div className="stat-card-value" style={{ color: '#92400E' }}>
          {stats.suspended}
        </div>
      </div>
      <div className="stat-card">
        <span className="stat-card-label">Left</span>
        <div className="stat-card-value" style={{ color: 'var(--ink-mute)' }}>
          {stats.left}
        </div>
      </div>
    </div>
  );
}

function FilterButton({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      className={`toolbar-filter ${active ? 'active' : ''}`}
      onClick={onClick}
      aria-pressed={active}
    >
      {label}
      {typeof count === 'number' && (
        <span style={{ opacity: 0.7 }}>({count})</span>
      )}
    </button>
  );
}

function SkeletonGrid() {
  return (
    <div className="staff-grid" aria-busy="true" aria-label="Loading staff">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="skeleton-card" />
      ))}
    </div>
  );
}

function StaffEmpty({ hasAnyStaff, searchTerm, statusFilter }) {
  if (!hasAnyStaff) {
    return (
      <EmptyState
        icon={
          <svg viewBox="0 0 64 64" width="36" height="36" aria-hidden="true">
            <circle cx="32" cy="22" r="10" />
            <path d="M14 54c2-10 10-16 18-16s16 6 18 16" />
          </svg>
        }
        title="No staff yet."
        body="Add your first staff member — name, role, photo. You'll get a unique verify URL they can share via a printed card or QR code."
        action={
          <Link to="/dashboard/staff/new" className="btn btn-primary">
            Add your first staff member
          </Link>
        }
      />
    );
  }

  // There are staff, but the filter returned nothing
  const reason = searchTerm
    ? `No matches for "${searchTerm}"`
    : `No ${statusFilter ?? ''} staff`;
  return (
    <EmptyState
      title={reason}
      body="Try a different search or clear your filters."
    />
  );
}

function LoadError({ onRetry }) {
  return (
    <div className="alert alert-error" role="alert">
      <strong>Couldn't load your staff list.</strong>{' '}
      Check your connection and{' '}
      <button type="button" className="btn btn-ghost" onClick={onRetry} style={{ padding: 0, textDecoration: 'underline' }}>
        try again
      </button>.
    </div>
  );
}
